import React, { useState, useEffect } from 'react';
import { Dictionary, apiService } from '../services/api';
import TeacherClasses from './TeacherClasses';
import TeacherAssignments from './TeacherAssignments';
import TeacherProgress from './TeacherProgress';

interface Student {
  id: number;
  username: string;
  email: string;
  created_at: string;
  assigned_at: string;
  notes?: string;
  assigned_by?: number;
  assigned_by_username?: string;
  total_games_played: number;
  avg_accuracy: number;
  last_played_at?: string;
  games_this_week: number;
}

interface AvailableUser {
  id: number;
  username: string;
  email: string;
  created_at: string;
  account_type: string;
  is_my_student: boolean;
  teacher_count: number;
}

interface StudentProgress {
  student: {
    id: number;
    username: string;
    email: string;
    created_at: string;
  };
  records: any[];
  stats: {
    total_games: number;
    unique_dictionaries: number;
    avg_accuracy: number;
    total_correct: number;
    total_wrong: number;
    total_pairs_attempted: number;
    highest_score: number;
    first_played: string;
    last_played: string;
  };
}

interface ClassAnalytics {
  classStats: {
    total_students: number;
    total_games_played: number;
    unique_dictionaries: number;
    avg_class_accuracy: number;
    active_students_week: number;
    active_students_month: number;
  };
  studentPerformance: Array<{
    id: number;
    username: string;
    games_played: number;
    avg_accuracy: number;
    best_score: number;
    last_played: string;
  }>;
  activityTimeline: Array<{
    date: string;
    games_count: number;
    unique_students: number;
    avg_accuracy: number;
  }>;
}

interface TeacherDashboardProps {
  onClose?: () => void;
  onSelectDictionary?: (dictionary: Dictionary) => void;
}

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ onClose, onSelectDictionary }) => {
  const [activeTab, setActiveTab] = useState<'students' | 'classes' | 'assignments' | 'progress' | 'analytics' | 'add'>('students');
  const [students, setStudents] = useState<Student[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [analytics, setAnalytics] = useState<ClassAnalytics | null>(null);
  const [selectedStudentProgress, setSelectedStudentProgress] = useState<StudentProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [addNotes, setAddNotes] = useState('');

  useEffect(() => {
    loadStudents();
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'add') {
      loadAvailableUsers();
    }
  }, [activeTab]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTeacherStudents();
      if (data.success) {
        setStudents(data.students);
      } else {
        setError(data.error || 'Failed to load students');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load students');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const data = await apiService.getAvailableStudents();
      if (data.success) {
        setAvailableUsers(data.users);
      }
    } catch (err) {
      console.error('Failed to load available users:', err);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await apiService.getTeacherAnalytics();
      if (data.success) {
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  };

  const loadStudentProgress = async (studentId: number) => {
    try {
      const data = await apiService.getStudentProgress(studentId);
      if (data.success) {
        setSelectedStudentProgress(data);
      }
    } catch (err) {
      console.error('Failed to load student progress:', err);
    }
  };

  const handleAddStudents = async () => {
    if (selectedUserIds.length === 0) {
      setError('Please select at least one user to add');
      return;
    }

    try {
      const data = await apiService.addStudents(selectedUserIds, addNotes || undefined);
      if (data.success) {
        setSelectedUserIds([]);
        setAddNotes('');
        setActiveTab('students');
        loadStudents();
        loadAvailableUsers();
      } else {
        setError(data.error || 'Failed to add students');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add students');
      console.error(err);
    }
  };

  const handleRemoveStudent = async (studentId: number) => {
    if (!window.confirm('Remove this student from your class?')) return;

    try {
      const data = await apiService.removeStudent(studentId);
      if (data.success) {
        setStudents(students.filter(s => s.id !== studentId));
        if (selectedStudentProgress?.student.id === studentId) {
          setSelectedStudentProgress(null);
        }
      } else {
        setError(data.error || 'Failed to remove student');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove student');
      console.error(err);
    }
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredAvailableUsers = availableUsers.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatAccuracy = (accuracy: number | null | undefined) => {
    if (accuracy === null || accuracy === undefined || isNaN(accuracy)) return 'N/A';
    return `${Number(accuracy).toFixed(1)}%`;
  };

  if (loading && students.length === 0) {
    return <div className="teacher-dashboard">Loading...</div>;
  }

  return (
    <div className="teacher-dashboard">
      <div className="teacher-header">
        <h2>👨‍🏫 Teacher Dashboard</h2>
        {onClose && (
          <button onClick={onClose} className="close-button">
            ✕ Close
          </button>
        )}
      </div>

      <div className="teacher-tabs">
        <button
          className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          📚 My Students ({students.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'classes' ? 'active' : ''}`}
          onClick={() => setActiveTab('classes')}
        >
          🏫 Classes
        </button>
        <button
          className={`tab-button ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          📝 Assignments
        </button>
        <button
          className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          📊 Progress
        </button>
        <button
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
        <button
          className={`tab-button ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          ➕ Add Students
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ margin: '1rem 0' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '1rem' }}>✕</button>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="students-tab">
          {selectedStudentProgress ? (
            <div className="student-progress-detail">
              <button
                onClick={() => setSelectedStudentProgress(null)}
                className="back-button"
                style={{ marginBottom: '1rem' }}
              >
                ← Back to Students
              </button>

              <h3>Progress for {selectedStudentProgress.student.username}</h3>

              <div className="teacher-stats-grid" style={{ marginTop: '1rem' }}>
                <div className="teacher-stat-card">
                  <div className="teacher-stat-label">Total Games</div>
                  <div className="teacher-stat-value">{selectedStudentProgress.stats.total_games || 0}</div>
                </div>
                <div className="teacher-stat-card">
                  <div className="teacher-stat-label">Average Accuracy</div>
                  <div className="teacher-stat-value">{formatAccuracy(selectedStudentProgress.stats.avg_accuracy)}</div>
                </div>
                <div className="teacher-stat-card">
                  <div className="teacher-stat-label">Highest Score</div>
                  <div className="teacher-stat-value">{selectedStudentProgress.stats.highest_score || 0}</div>
                </div>
                <div className="teacher-stat-card">
                  <div className="teacher-stat-label">Unique Dictionaries</div>
                  <div className="teacher-stat-value">{selectedStudentProgress.stats.unique_dictionaries || 0}</div>
                </div>
              </div>

              <h4 style={{ marginTop: '2rem' }}>Recent Games</h4>
              {selectedStudentProgress.records.length === 0 ? (
                <p>No games played yet.</p>
              ) : (
                <div className="game-records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Dictionary</th>
                        <th>Score</th>
                        <th>Accuracy</th>
                        <th>Correct/Wrong</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedStudentProgress.records.map((record, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(record.played_at)}</td>
                          <td>{record.dictionary_name}</td>
                          <td>{record.final_score}</td>
                          <td>{formatAccuracy(record.accuracy * 100)}</td>
                          <td>{record.correct_matches}/{record.wrong_matches}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <>
              <h3>My Students</h3>
              {students.length === 0 ? (
                <div className="empty-state">
                  <p>You haven't added any students yet.</p>
                  <button onClick={() => setActiveTab('add')} className="primary-button">
                    Add Students
                  </button>
                </div>
              ) : (
                <div className="students-grid">
                  {students.map(student => (
                    <div key={student.id} className="student-card">
                      <div className="student-header">
                        <h4>{student.username}</h4>
                        <span className="student-email">{student.email}</span>
                      </div>

                      <div className="student-stats">
                        <div className="stat-row">
                          <span>Games Played:</span>
                          <strong>{student.total_games_played || 0}</strong>
                        </div>
                        <div className="stat-row">
                          <span>Avg Accuracy:</span>
                          <strong>{formatAccuracy(student.avg_accuracy)}</strong>
                        </div>
                        <div className="stat-row">
                          <span>This Week:</span>
                          <strong>{student.games_this_week || 0} games</strong>
                        </div>
                        {student.last_played_at && (
                          <div className="stat-row">
                            <span>Last Played:</span>
                            <strong>{formatDate(student.last_played_at)}</strong>
                          </div>
                        )}
                      </div>

                      <div className="student-actions">
                        <button
                          onClick={() => loadStudentProgress(student.id)}
                          className="view-progress-button"
                        >
                          View Progress
                        </button>
                        <button
                          onClick={() => handleRemoveStudent(student.id)}
                          className="remove-button"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'analytics' && analytics && (
        <div className="analytics-tab">
          <h3>Class Analytics</h3>

          <div className="teacher-stats-grid">
            <div className="teacher-stat-card">
              <div className="teacher-stat-label">Total Students</div>
              <div className="teacher-stat-value">{analytics.classStats.total_students || 0}</div>
            </div>
            <div className="teacher-stat-card">
              <div className="teacher-stat-label">Total Games</div>
              <div className="teacher-stat-value">{analytics.classStats.total_games_played || 0}</div>
            </div>
            <div className="teacher-stat-card">
              <div className="teacher-stat-label">Class Avg Accuracy</div>
              <div className="teacher-stat-value">{formatAccuracy(analytics.classStats.avg_class_accuracy)}</div>
            </div>
            <div className="teacher-stat-card">
              <div className="teacher-stat-label">Active This Week</div>
              <div className="teacher-stat-value">{analytics.classStats.active_students_week || 0}</div>
            </div>
          </div>

          <h4 style={{ marginTop: '2rem' }}>Student Performance</h4>
          {analytics.studentPerformance.length === 0 ? (
            <p>No student activity yet.</p>
          ) : (
            <div className="performance-table">
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Games Played</th>
                    <th>Avg Accuracy</th>
                    <th>Best Score</th>
                    <th>Last Played</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.studentPerformance.map(perf => (
                    <tr key={perf.id}>
                      <td><strong>{perf.username}</strong></td>
                      <td>{perf.games_played || 0}</td>
                      <td>{formatAccuracy(perf.avg_accuracy)}</td>
                      <td>{perf.best_score || 0}</td>
                      <td>{formatDate(perf.last_played)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 style={{ marginTop: '2rem' }}>Activity Timeline (Last 30 Days)</h4>
          {analytics.activityTimeline.length === 0 ? (
            <p>No activity in the last 30 days.</p>
          ) : (
            <div className="activity-timeline">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Games</th>
                    <th>Students</th>
                    <th>Avg Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.activityTimeline.map((activity, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(activity.date)}</td>
                      <td>{activity.games_count}</td>
                      <td>{activity.unique_students}</td>
                      <td>{formatAccuracy(activity.avg_accuracy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'add' && (
        <div className="add-students-tab">
          <h3>Add Students to Your Class</h3>

          <div className="search-box" style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="selection-summary">
            <p><strong>{selectedUserIds.length}</strong> user(s) selected</p>
            {selectedUserIds.length > 0 && (
              <button onClick={() => setSelectedUserIds([])} className="clear-button">
                Clear Selection
              </button>
            )}
          </div>

          <div className="notes-input" style={{ marginBottom: '1rem' }}>
            <label>Optional Notes:</label>
            <textarea
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              placeholder="Add notes about these students..."
              rows={2}
              style={{ width: '100%', padding: '0.5rem' }}
            />
          </div>

          {selectedUserIds.length > 0 && (
            <button
              onClick={handleAddStudents}
              className="primary-button"
              style={{ marginBottom: '1rem' }}
            >
              Add {selectedUserIds.length} Student{selectedUserIds.length > 1 ? 's' : ''}
            </button>
          )}

          <div className="users-list">
            {filteredAvailableUsers.length === 0 ? (
              <p>No users found.</p>
            ) : (
              filteredAvailableUsers.map(user => (
                <div
                  key={user.id}
                  className={`user-item ${selectedUserIds.includes(user.id) ? 'selected' : ''} ${user.is_my_student ? 'already-student' : ''}`}
                  onClick={() => !user.is_my_student && toggleUserSelection(user.id)}
                  style={{ cursor: user.is_my_student ? 'not-allowed' : 'pointer' }}
                >
                  <div className="user-info">
                    <div className="user-name">
                      {selectedUserIds.includes(user.id) && '✓ '}
                      <strong>{user.username}</strong>
                      {user.is_my_student && <span className="badge">Already Your Student</span>}
                    </div>
                    <div className="user-details">
                      {user.email} • {user.account_type}
                      {user.teacher_count > 0 && ` • ${user.teacher_count} teacher(s)`}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Classes Tab */}
      {activeTab === 'classes' && <TeacherClasses />}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && <TeacherAssignments />}

      {/* Progress Tab */}
      {activeTab === 'progress' && <TeacherProgress />}
    </div>
  );
};

export default TeacherDashboard;
